/**
 * AGROMX Frontend - Push Notification Service
 * Registra Service Worker y gestiona suscripciones push
 * Basado en el patrón probado de FertiPlanta
 */

import api from './api.js';

class PushService {
  constructor() {
    this.isSubscribed = false;
    /** Evita registrar dos veces el listener de mensajes del SW */
    this._swMessageBound = false;
  }

  /**
   * Verificar si el navegador soporta push notifications
   */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /**
   * Inicializar: registrar SW y suscribirse automáticamente
   * Llamar después del login (desde main.js)
   */
  async init() {
    if (!this.isSupported()) {
      console.warn('[Push] No soportado en este navegador');
      return false;
    }

    try {
      // 1. Registrar Service Worker
      await navigator.serviceWorker.register('/sw-push.js');
      console.log('[Push] Service Worker registrado');

      // 2. Esperar a que el SW esté completamente activo (patrón FertiPlanta)
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service Worker activo, scope:', registration.scope);

      // Listener de click desde SW (tambien si la suscripcion ya existia)
      if (!this._swMessageBound) {
        this._swMessageBound = true;
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'PUSH_NOTIFICATION_CLICK') {
            this.handleNotificationClick(event.data.data);
          }
        });
      }

      // 3. Verificar si ya hay suscripción existente
      let subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        console.log('[Push] Suscripción existente encontrada');
        this.isSubscribed = true;
        await this.enviarSuscripcionAlServidor(subscription);
        return true;
      }

      // 4. No hay suscripción — solicitar permiso
      const permiso = await Notification.requestPermission();
      if (permiso !== 'granted') {
        console.warn('[Push] Permiso denegado');
        return false;
      }

      // 5. Obtener VAPID public key del servidor (misma base URL que api.post para evitar CORS/host distintos)
      const publicKey = await this.obtenerVapidPublicKey();
      if (!publicKey) {
        console.error('[Push] No se pudo obtener la VAPID public key');
        return false;
      }

      // 6. Suscribirse al push service (breve pausa: SW a veces no aplica pushManager hasta el siguiente tick)
      await new Promise((r) => setTimeout(r, 150));
      console.log('[Push] Suscribiendo con VAPID key...');
      let applicationServerKey;
      try {
        applicationServerKey = this.urlBase64ToUint8Array(publicKey);
      } catch (e) {
        console.error('[Push] Clave VAPID invalida (revisa .env sin saltos de linea):', e?.message || e);
        return false;
      }

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      console.log('[Push] Suscripción exitosa:', subscription.endpoint.substring(0, 60) + '...');

      // 7. Registrar en el servidor
      await this.enviarSuscripcionAlServidor(subscription);
      this.isSubscribed = true;

      return true;
    } catch (error) {
      console.error('[Push] Error:', error.name, error.message);
      return false;
    }
  }

  /**
   * Obtener VAPID public key del servidor (usa api.js: misma base URL y token que /push/subscribe)
   */
  async obtenerVapidPublicKey() {
    try {
      const res = await api.get('/push/public-key');
      const raw = res?.data?.publicKey ?? res?.publicKey ?? '';
      const key = String(raw).replace(/\s+/g, '').trim();
      console.log('[Push] VAPID public key recibida:', key ? `${key.substring(0, 20)}...` : 'VACIA');
      return key;
    } catch (error) {
      console.error('[Push] Error obteniendo public key:', error?.message || error);
      return '';
    }
  }

  /**
   * Re-envia la suscripcion del navegador al backend (reintento post-login si init fallo a mitad de flujo)
   */
  async syncSubscriptionToServer() {
    if (!this.isSupported()) return false;
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return false;
      console.log('[Push] Re-sincronizando suscripcion con servidor...');
      await this.enviarSuscripcionAlServidor(subscription);
      this.isSubscribed = true;
      return true;
    } catch (e) {
      console.warn('[Push] syncSubscriptionToServer:', e?.message || e);
      return false;
    }
  }

  /**
   * Enviar suscripción al backend
   */
  async enviarSuscripcionAlServidor(subscription) {
    await api.post('/push/subscribe', { subscription: subscription.toJSON() });
    console.log('[Push] Suscripción registrada en servidor');
  }

  /**
   * Solo quita la suscripcion del navegador (sin API). Sirve cuando la sesion ya no es valida (p. ej. 401).
   */
  async unsubscribeBrowserOnly() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      this.isSubscribed = false;
    } catch (error) {
      console.error('[Push] Error al desuscribir solo navegador:', error);
    }
  }

  /**
   * Cancelar suscripcion en servidor y en el navegador
   */
  async unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await api.post('/push/unsubscribe', { endpoint });
        await subscription.unsubscribe();
      } else {
        await api.post('/push/unsubscribe', {});
      }
      this.isSubscribed = false;
    } catch (error) {
      console.error('[Push] Error al desuscribirse:', error);
    }
  }

  /**
   * Manejar click en notificación (mensaje del SW)
   */
  handleNotificationClick(data) {
    if (window.trackingView && data.lat && data.lng) {
      const map = window.trackingView.data?.map;
      if (map) {
        map.panTo({ lat: parseFloat(data.lat), lng: parseFloat(data.lng) });
        map.setZoom(16);

        const infoWindow = window.trackingView.data?.infoWindow;
        if (infoWindow) {
          infoWindow.setContent(`
            <div style="font-family:inherit; padding:4px; min-width:180px;">
              <div style="font-weight:bold; font-size:13px;">${data.vehiculo || ''}</div>
              <div style="font-size:12px; ${data.tipo === 'alerta' ? 'color:#dc3545;' : ''}">
                <strong>${data.evento || ''}</strong>
              </div>
            </div>
          `);
          infoWindow.setPosition({ lat: parseFloat(data.lat), lng: parseFloat(data.lng) });
          infoWindow.open(map);
        }
      }
    }
  }

  /**
   * Convertir VAPID key de base64url a Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export default new PushService();
